// src/pages/projects/ProjectViewer.js
import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { Container, Row, Col, Alert, Badge } from 'react-bootstrap'
import { getProjectById } from '../../config/projects'
import { meta } from '../../content_option'
import './style.css'

export const ProjectViewer = () => {
  const { projectId } = useParams()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [iframeLoaded, setIframeLoaded] = useState(false)

  useEffect(() => {
    const foundProject = getProjectById(projectId)
    
    if (foundProject) {
      setProject(foundProject)
      setError(null)
    } else {
      setError('Project not found')
    }
    
    setLoading(false)
  }, [projectId])

  useEffect(() => {
    if (project && project.display.launchType !== 'embedded') {
      window.location.href = project.deployment.url
    }
  }, [project])

  const handleIframeLoad = () => {
    setIframeLoaded(true)
  }

  const handleIframeError = () => {
    setError('Failed to load project. The project might be temporarily unavailable.')
  }

  if (loading) {
    return (
      <div className="project-viewer loading">
        <Container>
          <div className="text-center py-5">
            <h3>Loading project...</h3>
          </div>
        </Container>
      </div>
    )
  }

  if (error || !project) {
    return (
      <HelmetProvider>
        <div className="project-viewer error">
          <Helmet>
            <meta charSet="utf-8" />
            <title>Project Not Found | {meta.title}</title>
          </Helmet>
          <Container>
            <Row className="justify-content-center">
              <Col lg="8" className="text-center py-5">
                <Link to="/projects" className="back-link mb-3 d-inline-block">
                  ← Back to Projects
                </Link>
                <Alert variant="danger">
                  <h4>Project Not Found</h4>
                  <p>{error || `The project "${projectId}" could not be found.`}</p>
                  <Link to="/projects" className="btn btn-primary">
                    View All Projects
                  </Link>
                </Alert>
              </Col>
            </Row>
          </Container>
        </div>
      </HelmetProvider>
    )
  }

  if (project && project.display.launchType !== 'embedded') {
    return (
      <HelmetProvider>
        <div className="project-viewer redirect">
          <Helmet>
            <meta charSet="utf-8" />
            <title>{project.title} | {meta.title}</title>
          </Helmet>
          <Container>
            <Row className="justify-content-center">
              <Col lg="8" className="text-center py-5">
                <h3>Redirecting to {project.title}...</h3>
                <p>
                  If you're not redirected automatically, 
                  <a href={project.deployment.url} target="_blank" rel="noopener noreferrer">
                    click here
                  </a>
                </p>
              </Col>
            </Row>
          </Container>
        </div>
      </HelmetProvider>
    )
  }

  return (
    <HelmetProvider>
      <div className="project-viewer embedded">
        <Helmet>
          <meta charSet="utf-8" />
          <title>{project.title} | {meta.title}</title>
          <meta name="description" content={project.description} />
        </Helmet>

        {/* Project Header */}
        <div className="project-header">
          <Container>
            <Row>
              <Col lg="12">
                <div className="header-content">
                  <Link to="/projects" className="back-link">
                    ← Back to Projects
                  </Link>
                  <div className="project-info">
                    <h1 className="project-title">{project.title}</h1>
                    <p className="project-description">{project.description}</p>
                    <div className="project-meta">
                      <Badge 
                        bg={project.status === 'live' ? 'success' : 'warning'}
                        className="me-2"
                      >
                        {project.status}
                      </Badge>
                      <span className="version">v{project.version}</span>
                      <a 
                        href={project.deployment.repository}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="repo-link ms-3"
                      >
                        📝 View Source
                      </a>
                      <a 
                        href={project.deployment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="external-link ms-3"
                      >
                        🔗 Open in New Tab
                      </a>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* Embedded Project */}
        <div className="embedded-project">
          {!iframeLoaded && (
            <div className="iframe-loading">
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading {project.title}...</p>
              </div>
            </div>
          )}
          
          <iframe
            src={project.deployment.embedUrl || project.deployment.url}
            title={project.title}
            className={`project-iframe ${iframeLoaded ? 'loaded' : ''}`}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
            loading="lazy"
          />
        </div>

        {/* Project Footer */}
        <div className="project-footer">
          <Container>
            <Row>
              <Col lg="12" className="text-center">
                <div className="footer-content">
                  <div className="tech-stack">
                    <strong>Built with:</strong>
                    {project.technologies.map(tech => (
                      <span key={tech} className="tech-tag ms-2">{tech}</span>
                    ))}
                  </div>
                  <div className="project-actions mt-3">
                    <a 
                      href={project.deployment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary me-2"
                    >
                      🚀 Launch Full App
                    </a>
                    <a 
                      href={project.deployment.repository}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-secondary"
                    >
                      📝 View Code
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </HelmetProvider>
  )
}